/**
 * operationalContext.js — Kevia Context Engine: Operational Layer
 *
 * Pre-loads the user's pastoral reality when Kevia opens.
 * Runs 4 data tools in parallel (scoped by user permissions) and
 * caches the result as a compact "[panorama: ...]" context string.
 *
 * This is the ambient-awareness layer — Kevia always knows what is
 * happening in the district/church WITHOUT waiting for a specific intent.
 *
 * Design:
 *  - Never blocks the UI — loads asynchronously after Kevia mounts
 *  - Respects all existing RLS, scope, and permission rules
 *  - Falls back silently to null when offline or scope is missing
 *  - Cache TTL: 5 min per scope fingerprint (in-memory only)
 *  - Invalidation: called after any successful mutation
 */

import { executeTool }          from "../kevia-data/executeTool";
import { compactDataContext }    from "../kevia-data/compactDataContext";
import { synthesizeContext }     from "../kevia-data/contextSynthesizer";
import { updateDistrictState }   from "../kevia-data/districtStateEngine";

const SNAPSHOT_TTL_MS    = 5 * 60 * 1000;  // 5 min
const MAX_SNAPSHOT_CHARS = 700;

// In-memory cache keyed by scope fingerprint — never persisted.
const _cache = new Map();

function _key(appScope) {
  return `${appScope?.district_id ?? "_"}:${appScope?.church_id ?? "_"}:${appScope?.role ?? "_"}`;
}

/**
 * Tools that run in parallel when Kevia opens.
 * Provides full district ambient awareness.
 *
 * executeTool handles per-tool role checks — if a tool isn't allowed for
 * the current role it returns { ok: false } and is silently skipped.
 */
const SNAPSHOT_TOOLS = [
  "get_today_visits",
  "get_upcoming_events",
  "get_recent_prayers",
  "get_people_needing_followup",
  "get_church_activity_summary",
  "get_upcoming_visits",
  "get_member_status",
  "get_upcoming_preachings",
];

/* ─── Public API ────────────────────────────────────────────── */

/**
 * Load and cache the operational snapshot for the current scope.
 * Returns a compact "[panorama: ...]" string, or null if nothing relevant.
 *
 * @param {Object} appScope — from useKeviaContext().appScope
 * @returns {Promise<string|null>}
 */
export async function loadOperationalSnapshot(appScope) {
  if (!appScope) return null;
  if (!appScope.district_id && !appScope.church_id) return null;

  const key    = _key(appScope);
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < SNAPSHOT_TTL_MS) return cached.snapshot;

  try {
    const settled = await Promise.allSettled(
      SNAPSHOT_TOOLS.map((toolId) => executeTool({ toolId, filters: {}, appScope }))
    );

    const results = settled
      .filter((r) => r.status === "fulfilled")
      .map((r) => r.value)
      .filter((r) => r.ok);

    if (!results.length) {
      _cache.set(key, { snapshot: null, ts: Date.now() });
      return null;
    }

    // Try synthesis-based panorama first — richer, includes pastoral insights.
    const { compact: synthesisCompact, ministryState, insights } = synthesizeContext({ results, appScope });

    // Update live district state engine with freshly synthesized data.
    // This keeps districtStateEngine current without recomputing synthesizeContext.
    if (ministryState) {
      updateDistrictState({ ministryState, insights }, appScope);
    }

    let snapshot = synthesisCompact
      ? synthesisCompact.replace("[synthesis:", "[panorama:").slice(0, MAX_SNAPSHOT_CHARS)
      : null;

    // Fallback: concatenated compactDataContext if synthesis yields nothing
    if (!snapshot) {
      const raw = compactDataContext(results);
      if (!raw) {
        _cache.set(key, { snapshot: null, ts: Date.now() });
        return null;
      }
      snapshot = raw.replace(/^\[data:/, "[panorama:").slice(0, MAX_SNAPSHOT_CHARS);
    }

    _cache.set(key, { snapshot, ts: Date.now() });
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Invalidate the cached snapshot for this scope.
 * Call after any mutation that would change visit/prayer/event counts.
 *
 * @param {Object} appScope
 */
export function invalidateOperationalSnapshot(appScope) {
  if (!appScope) return;
  _cache.delete(_key(appScope));
}

/**
 * Return the cached snapshot synchronously without triggering a load.
 * Returns null if the cache is expired or empty.
 *
 * @param {Object} appScope
 * @returns {string|null}
 */
export function getCachedOperationalSnapshot(appScope) {
  if (!appScope) return null;
  const cached = _cache.get(_key(appScope));
  if (!cached || Date.now() - cached.ts >= SNAPSHOT_TTL_MS) return null;
  return cached.snapshot;
}

/**
 * Clear all cached snapshots regardless of scope.
 * Called by useKeviaChat when liveEventBridge fires MUTATION_SUCCESS —
 * mutations affect scope-level counts so the whole cache is stale.
 */
export function clearOperationalSnapshotCache() {
  _cache.clear();
}
